if (!customElements.get('dd-editorial-four-card')) {
  customElements.define('dd-editorial-four-card', class DDEditorialFourCard extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.playRequests = new WeakMap();
      this.motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.mobileOrderQuery = window.matchMedia?.('(max-width: 989.98px)');
      this.sourceCards ||= [...this.children].filter((card) => card.matches('.dd-editorial-four-card__card'));
      this.applyCardOrder();
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.querySelectorAll('video').forEach((video) => {
        video.muted = video.closest('.dd-editorial-four-card__card--media')?.dataset.ddUserUnmuted !== 'true';
        video.playsInline = true;
        video.addEventListener('playing', () => this.onPlaying(video), { signal });
        video.addEventListener('pause', () => this.sync(video, false, video.closest('.dd-editorial-four-card__card--media')?.hasAttribute('data-dd-video-error')), { signal });
        video.addEventListener('ended', () => this.sync(video, false, false, true), { signal });
        video.addEventListener('error', () => this.sync(video, false, true, true), { signal });
        video.querySelectorAll('source').forEach((source) => {
          source.addEventListener('error', () => {
            if (!source.hasAttribute('src')) return;
            source.dataset.ddEditorialFailed = 'true';
            if ([...video.querySelectorAll('source[src]')].every((candidate) => candidate.dataset.ddEditorialFailed === 'true')) {
              video.pause();
              this.sync(video, false, true, true);
            }
          }, { signal });
        });
      });
      this.motionQuery?.addEventListener?.('change', () => {
        if (this.motionQuery.matches) this.autoplayVideos().forEach((video) => video.pause());
        else this.updateVisibility();
      }, { signal });
      this.mobileOrderQuery?.addEventListener?.('change', () => {
        this.applyCardOrder();
        this.updateVisibility();
      }, { signal });
      if (typeof window.IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
          this.syncVisibility(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.1);
        }), { threshold: [0, 0.1] });
        this.querySelectorAll('video').forEach((video) => this.observer.observe(video));
      } else {
        window.addEventListener('scroll', () => this.queueVisibilityCheck(), { passive: true, capture: true, signal });
      }
      window.addEventListener('resize', () => this.queueVisibilityCheck(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.updateVisibility(), { signal });
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.abortController?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      this.querySelectorAll('.dd-editorial-four-card__card--media').forEach((card) => {
        clearTimeout(card.ddControlsTimer);
        clearTimeout(card.ddExternalVideoTimer);
      });
    }

    applyCardOrder() {
      const cards = (this.sourceCards || []).filter((card) => card.parentElement === this);
      const orderedCards = this.mobileOrderQuery?.matches
        ? cards
          .map((card, index) => {
            const savedPosition = Number.parseInt(card.dataset.ddMobilePosition, 10);
            return { card, index, position: Number.isInteger(savedPosition) ? savedPosition : index + 1 };
          })
          .sort((a, b) => a.position - b.position || a.index - b.index)
          .map(({ card }) => card)
        : cards;
      orderedCards.forEach((card) => this.append(card));
    }

    onClick(event) {
      const externalPoster = event.target.closest('[data-dd-external-video-poster]');
      if (externalPoster && this.contains(externalPoster)) {
        this.openExternalVideo(externalPoster);
        return;
      }
      const playControl = event.target.closest('[data-dd-editorial-playback]');
      const soundControl = event.target.closest('[data-dd-editorial-sound]');
      if (!playControl && !soundControl) return;
      const card = event.target.closest('.dd-editorial-four-card__card--media');
      const video = card?.querySelector('video');
      if (!video) return;
      if (soundControl) {
        video.muted = !video.muted;
        card.dataset.ddUserUnmuted = String(!video.muted);
        this.syncSound(card, video);
        this.reveal(card);
      } else if (video.paused) this.play(video, true);
      else {
        video.dataset.ddUserPaused = 'true';
        video.pause();
      }
    }

    openExternalVideo(poster) {
      const card = poster.closest('.dd-editorial-four-card__card--external-video');
      const iframe = card?.querySelector('iframe[data-src]');
      if (!card || !iframe || poster.disabled) return;

      document.querySelectorAll('dd-editorial-four-card video').forEach((video) => video.pause());
      clearTimeout(card.ddExternalVideoTimer);
      poster.disabled = true;
      poster.setAttribute('aria-busy', 'true');
      card.setAttribute('data-dd-external-video-loading', '');

      const revealPlayer = () => {
        clearTimeout(card.ddExternalVideoTimer);
        card.removeAttribute('data-dd-external-video-loading');
        card.setAttribute('data-dd-external-video-ready', '');
        poster.removeAttribute('aria-busy');
        iframe.removeAttribute('aria-hidden');
        iframe.removeAttribute('tabindex');
        requestAnimationFrame(() => iframe.focus({ preventScroll: true }));
      };

      iframe.addEventListener('load', revealPlayer, { once: true, signal: this.abortController.signal });
      iframe.src = iframe.dataset.src;
      card.ddExternalVideoTimer = setTimeout(() => {
        if (card.hasAttribute('data-dd-external-video-ready')) return;
        iframe.removeAttribute('src');
        card.removeAttribute('data-dd-external-video-loading');
        poster.disabled = false;
        poster.removeAttribute('aria-busy');
      }, 12000);
    }

    onPointerUp(event) {
      if (event.pointerType !== 'touch' || event.target.closest('button, a')) return;
      const card = event.target.closest('.dd-editorial-four-card__card--media[data-playing]');
      if (card) this.reveal(card);
    }

    play(video, deliberate) {
      const card = video.closest('.dd-editorial-four-card__card--media');
      if (!this.isConnected || card?.dataset.ddEditorialPlaybackMode === 'off') return;
      if (!deliberate && (!this.isAutoplay(video) || this.motionQuery?.matches || video.dataset.ddUserPaused === 'true' || card?.hasAttribute('data-dd-video-error') || !this.isVisible(video))) return;
      if (deliberate) {
        document.querySelectorAll('dd-editorial-four-card video').forEach((candidate) => {
          if (candidate !== video) candidate.pause();
        });
        delete video.dataset.ddUserPaused;
        video.dataset.ddDeliberatePlay = 'true';
      }
      if (this.playRequests.has(video)) return;
      if (!video.paused && !video.ended) return;
      if (!this.hydrate(video, deliberate)) return;
      if (!deliberate || card?.dataset.ddUserUnmuted !== 'true') video.muted = true;
      try {
        const { signal } = this.abortController;
        const promise = Promise.resolve(video.play());
        this.playRequests.set(video, promise);
        promise.then(() => {
          if (!signal.aborted && video.closest('dd-editorial-four-card') === this && !card?.hasAttribute('data-playing')) this.onPlaying(video);
        }).catch((error) => {
          if (signal.aborted || !this.isConnected) return;
          if (error.name === 'AbortError') this.queueVisibilityCheck();
          else this.sync(video, false, true, true);
        }).finally(() => {
          if (this.playRequests.get(video) === promise) this.playRequests.delete(video);
        });
      } catch {
        this.sync(video, false, true, true);
      }
    }

    hydrate(video, deliberate) {
      if (video.closest('.dd-editorial-four-card__card--media')?.dataset.ddEditorialPlaybackMode === 'off') return false;
      const sources = [...video.querySelectorAll('source[data-dd-editorial-src]')];
      if (sources.length) {
        sources.forEach((source) => {
          source.src = source.dataset.ddEditorialSrc;
          delete source.dataset.ddEditorialSrc;
        });
        video.preload = 'metadata';
        video.load();
      } else if (deliberate && (video.error || video.closest('.dd-editorial-four-card__card--media')?.hasAttribute('data-dd-video-error'))) {
        video.querySelectorAll('source').forEach((source) => { delete source.dataset.ddEditorialFailed; });
        video.load();
      }
      return !!video.getAttribute('src') || !!video.querySelector('source[src]');
    }

    isVisible(video) {
      if (!this.isConnected || document.visibilityState === 'hidden') return false;
      const card = video.closest('.dd-editorial-four-card__card--media');
      if (card?.checkVisibility && !card.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
      const rect = video.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      return width * height >= rect.width * rect.height * 0.1;
    }

    syncVisibility(video, visible) {
      if (!visible || !this.isVisible(video)) video.pause();
      else if (this.isAutoplay(video)) this.play(video, false);
    }

    updateVisibility() {
      this.querySelectorAll('video').forEach((video) => this.syncVisibility(video, this.isVisible(video)));
    }

    queueVisibilityCheck() {
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = requestAnimationFrame(() => this.updateVisibility());
    }

    onPlaying(video) {
      if (!this.isVisible(video) || video.closest('dd-editorial-four-card') !== this) {
        video.pause();
        return;
      }
      const card = video.closest('.dd-editorial-four-card__card--media');
      card?.setAttribute('data-dd-video-ready', '');
      this.sync(video, true);
      if (video.dataset.ddDeliberatePlay === 'true') {
        delete video.dataset.ddDeliberatePlay;
        video.dispatchEvent(new CustomEvent('dd:media-play', { bubbles: true }));
      }
      if (card) this.hideLater(card);
    }

    sync(video, playing, failed = false, restorePoster = false) {
      const card = video.closest('.dd-editorial-four-card__card--media');
      const control = card?.querySelector('[data-dd-editorial-playback]');
      if (!card) return;
      if (restorePoster) card.removeAttribute('data-dd-video-ready');
      card.toggleAttribute('data-playing', playing);
      card.toggleAttribute('data-dd-video-error', failed);
      if (control) {
        control.setAttribute('aria-pressed', String(playing));
        control.setAttribute('aria-label', playing ? control.dataset.ddPauseLabel : control.dataset.ddPlayLabel);
      }
      this.syncSound(card, video);
      if (!playing) card.setAttribute('data-dd-controls-visible', '');
    }

    syncSound(card, video) {
      const control = card.querySelector('[data-dd-editorial-sound]');
      if (!control) return;
      control.setAttribute('aria-pressed', String(!video.muted));
      control.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      const label = control.querySelector('[data-dd-sound-label]');
      if (label) label.textContent = video.muted ? 'Sound off' : 'Sound on';
    }

    reveal(card) {
      card.setAttribute('data-dd-controls-visible', '');
      this.hideLater(card);
    }

    hideLater(card) {
      clearTimeout(card.ddControlsTimer);
      card.ddControlsTimer = setTimeout(() => {
        if (card.hasAttribute('data-playing') && !card.matches(':focus-within')) card.removeAttribute('data-dd-controls-visible');
      }, 2200);
    }

    autoplayVideos() {
      return [...this.querySelectorAll('video')].filter((video) => this.isAutoplay(video));
    }

    isAutoplay(video) {
      return video.closest('.dd-editorial-four-card__card--media')?.dataset.ddEditorialPlaybackMode === 'autoplay';
    }
  });
}
